import React, { useEffect, useRef, useState } from "react";
import RecordingManager from "../recording/RecordingManager";
import { getSteamDeepLinkFromStoreUrl } from "../../utils/steamUtils";
import "./SteamGameLauncher.scss";

function SteamGameLauncher({ assignment, onClose, onError }) {
  const recordingManagerRef = useRef(null);
  const screenCaptureStreamRef = useRef(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingStatus, setRecordingStatus] = useState("idle");
  const [localError, setLocalError] = useState(null);

  const steamStoreUrl = assignment?.webUrl || "";
  const steamDeepLink = getSteamDeepLinkFromStoreUrl(steamStoreUrl);

  const notifyError = (message) => {
    setLocalError(message);
    if (onError) {
      onError(message);
    }
  };

  const openSteam = () => {
    if (steamDeepLink) {
      window.location.href = steamDeepLink;
      return;
    }

    if (steamStoreUrl) {
      window.open(steamStoreUrl, "_blank", "noopener,noreferrer");
      return;
    }

    notifyError("Steam URL is missing for this assignment.");
  };

  const clearScreenCaptureStream = () => {
    if (screenCaptureStreamRef.current) {
      screenCaptureStreamRef.current.getTracks().forEach((track) => track.stop());
      screenCaptureStreamRef.current = null;
    }
  };

  const ensureScreenCapturePermission = async () => {
    // Request screen capture immediately from the user click handler
    // so browsers keep the user-gesture context and show the permission prompt.
    if (screenCaptureStreamRef.current) {
      const track = screenCaptureStreamRef.current.getVideoTracks?.()[0];
      if (track && track.readyState === "live") {
        return;
      }
      clearScreenCaptureStream();
    }

    const stream = await navigator.mediaDevices.getDisplayMedia({
      video: {
        mediaSource: "screen",
        width: { ideal: 1920 },
        height: { ideal: 1080 },
      },
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });

    const videoTrack = stream.getVideoTracks?.()[0];
    const displaySurface = videoTrack?.getSettings?.().displaySurface;

    // Steam sessions must capture an actual app window or full monitor.
    // "browser" (tab capture) usually stops as soon as user switches away to Steam.
    if (displaySurface === "browser") {
      stream.getTracks().forEach((track) => track.stop());
      throw new Error(
        "Please share an entire screen or app window (not a browser tab) so Steam gameplay can be recorded."
      );
    }

    screenCaptureStreamRef.current = stream;

    if (videoTrack) {
      videoTrack.onended = () => {
        clearScreenCaptureStream();
      };
    }
  };

  const startSession = async () => {
    if (!recordingManagerRef.current || isRecording || recordingStatus === "starting") {
      return;
    }

    setLocalError(null);
    setRecordingStatus("starting");
    try {
      await ensureScreenCapturePermission();
      await recordingManagerRef.current.start();
      setRecordingStatus("recording");
      setIsRecording(true);
      openSteam();
    } catch (err) {
      const message = err?.message || "Failed to start session recording.";
      setRecordingStatus("error");
      notifyError(message);
    }
  };

  const stopSession = async () => {
    if (
      !recordingManagerRef.current ||
      !isRecording ||
      recordingStatus === "stopping"
    ) {
      return;
    }

    setRecordingStatus("stopping");
    try {
      await recordingManagerRef.current.stop();
      setIsRecording(false);
      setRecordingStatus("stopped");
      clearScreenCaptureStream();
    } catch (err) {
      const message = err?.message || "Failed to stop session recording.";
      setRecordingStatus("error");
      notifyError(message);
    }
  };

  const handleClose = async () => {
    try {
      if (isRecording) {
        await stopSession();
      }
    } finally {
      if (onClose) {
        onClose();
      }
    }
  };

  useEffect(() => {
    // Cleanup only on unmount; do not tie this to isRecording state changes
    // because that can prematurely stop the shared screen stream.
    return () => {
      if (recordingManagerRef.current) {
        recordingManagerRef.current.stop().catch(() => {});
      }
      clearScreenCaptureStream();
    };
  }, []);

  return (
    <div className="steam-game-launcher">
      <RecordingManager
        ref={recordingManagerRef}
        assignmentId={assignment.assignmentId || assignment._id}
        gameId={assignment.gameId}
        username={assignment.username}
        getScreenCaptureStream={() => screenCaptureStreamRef.current}
        onRecordingStarted={() => {
          setIsRecording(true);
          setRecordingStatus("recording");
        }}
        onRecordingStopped={(earnings) => {
          setIsRecording(false);
          setRecordingStatus("stopped");
        }}
        onError={(message) => {
          setRecordingStatus("error");
          notifyError(message);
        }}
      />

      <div className="steam-launcher-panel">
        <h3>{assignment?.gameName || "Steam Game"} Session</h3>
        <p className="steam-launcher-subtitle">
          Start recording here, then launch and play in Steam. Return to this window to end your session.
        </p>

        <div className="steam-instructions">
          <p>1. Click Start Session (camera/mic/screen permission prompts will appear).</p>
          <p>2. In screen share picker, choose Entire Screen or a Window (not Browser Tab).</p>
          <p>3. Steam opens your game. Play as normal.</p>
          <p>4. Return here and click End Session when done.</p>
        </div>

        <div className="steam-status-row">
          <span className={`status-dot ${isRecording ? "recording" : ""}`} />
          <span className="status-text">
            {isRecording
              ? "Recording"
              : recordingStatus === "starting"
              ? "Starting..."
              : recordingStatus === "stopping"
              ? "Stopping..."
              : "Not Recording"}
          </span>
        </div>

        {localError && <div className="steam-error">{localError}</div>}

        <div className="steam-actions">
          {!isRecording ? (
            <button
              className="btn btn-primary"
              onClick={startSession}
              disabled={recordingStatus === "starting"}
            >
              Start Session & Open Steam
            </button>
          ) : (
            <button
              className="btn btn-danger"
              onClick={stopSession}
              disabled={recordingStatus === "stopping"}
            >
              {recordingStatus === "stopping" ? "Ending Session..." : "End Session"}
            </button>
          )}

          <button className="btn btn-secondary" onClick={openSteam}>
            Open Steam Again
          </button>

          <button className="btn btn-secondary" onClick={handleClose}>
            Close
          </button>
        </div>

      </div>
    </div>
  );
}

export default SteamGameLauncher;
